import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAward, FiInbox, FiSearch, FiSlash, FiArrowRight, FiDownload, FiFileText } from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  atosOficiaisService, TIPO_ATO_LABEL, TIPOS_ATO_LISTA,
  type AtoListaItem, type TipoAto,
} from '../../services/atosOficiais.service';
import { formatarDataBR } from '../../lib/data';

export function AtosOficiaisPublicoPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [tipo, setTipo] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [atos, setAtos] = useState<AtoListaItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Debounce da busca
  useEffect(() => {
    const id = setTimeout(() => setBuscaDebounced(busca), 300);
    return () => clearTimeout(id);
  }, [busca]);

  // Carregar atos sempre que filtros mudarem
  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const id = getEstabelecimentoAtualId();
    atosOficiaisService.listar(id, ano, tipo || undefined, buscaDebounced || undefined)
      .then((r) => setAtos(r.atos))
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [ano, tipo, buscaDebounced]);

  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];

  const totalPorTipo = useMemo(() => {
    const m: Record<string, number> = {};
    atos.forEach((a) => {
      const t = a.tipo_ato ?? 'outros';
      m[t] = (m[t] ?? 0) + 1;
    });
    return m;
  }, [atos]);

  return (
    <PortalPage
      titulo="Atos oficiais"
      subtitulo="Portarias, decretos, leis, resoluções e demais atos publicados."
      icone={<FiFileText size={26} />}
      largura="larga"
      voltarHref="/"
      voltarLabel="Voltar ao portal"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Atos oficiais' },
      ]}
    >
      <div className="space-y-5">
        {/* Filtros */}
        <SectionCard titulo="Filtros" icone={<FiSearch size={18} />}>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm font-medium"
            >
              {opcoesAno.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            >
              <option value="">Todos os tipos</option>
              {TIPOS_ATO_LISTA.map((t) => (
                <option key={t} value={t}>
                  {TIPO_ATO_LABEL[t]}
                  {totalPorTipo[t] ? ` (${totalPorTipo[t]})` : ''}
                </option>
              ))}
            </select>
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por número, ementa ou título..."
                className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm"
              />
            </div>
          </div>
        </SectionCard>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : erro ? (
          <Alert variante="erro">{erro}</Alert>
        ) : atos.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">
                {busca || tipo
                  ? 'Nenhum ato encontrado com os filtros atuais.'
                  : `Nenhum ato oficial publicado em ${ano}.`}
              </p>
            </div>
          </SectionCard>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              {atos.length} {atos.length === 1 ? 'ato encontrado' : 'atos encontrados'}
            </div>
            {atos.map((a) => (
              <Link
                key={a.id}
                to={`/transparencia/atos-oficiais/${a.id}`}
                className="block bg-white border border-slate-200 rounded-xl hover:border-slate-400 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-4 p-4">
                  <div className={`flex-shrink-0 mt-0.5 ${a.revogado ? 'text-red-400' : 'text-slate-400'}`}>
                    {a.revogado ? <FiSlash size={18} /> : <FiAward size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">
                        {a.tipo_ato ? TIPO_ATO_LABEL[a.tipo_ato as TipoAto] : 'Ato'} nº {a.numero_publicado}
                      </span>
                      {a.revogado && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">
                          REVOGADO
                        </span>
                      )}
                      {a.alterado_por_numero && !a.revogado && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                          ALTERADO
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 mt-1 line-clamp-2">
                      {a.ementa || a.titulo}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                      {a.data_publicacao && (
                        <span>Publicado em {formatarDataBR(a.data_publicacao)}</span>
                      )}
                      {a.autoridade_nome && <span>· {a.autoridade_nome}</span>}
                      {a.pdf_url && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <FiDownload size={11} /> PDF
                        </span>
                      )}
                      {a.revogado && a.revogado_por_numero && (
                        <span className="text-red-700">· Revogado por {a.revogado_por_numero}</span>
                      )}
                    </div>
                  </div>
                  <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PortalPage>
  );
}
